"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Camera, FileText, MapPin, ArrowRight, ShieldCheck, Activity } from "lucide-react";
export default function DigitalDirtLogs() {
  return <section id="digital-dirt-logs" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {/* Subtle background layer */}
      <div className="absolute inset-0 bg-secondary/30" />
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none text-foreground" style={{
      backgroundImage: "radial-gradient(circle at center, currentColor 1px, transparent 1px)",
      backgroundSize: "32px 32px"
    }} />
      
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background pointer-events-none" />

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-8 items-center">
          
          {/* Left Column: Device Mockups */}
          <div className="lg:col-span-7 relative">
            {/* Glow effect behind devices */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-primary/10 blur-[100px] rounded-full pointer-events-none" />

            {/* Tablet Frame (Portal Dashboard) */}
            <div className="relative w-full max-w-[720px] aspect-[16/10] rounded-2xl border border-border/50 bg-card shadow-2xl overflow-hidden transform transition-transform hover:scale-[1.01] duration-500">
              {/* Browser-style top bar */}
              <div className="absolute top-0 inset-x-0 h-10 bg-secondary/80 backdrop-blur-sm border-b border-border/50 flex items-center px-4 gap-2 z-20">
                <div className="size-2.5 rounded-full bg-destructive/70" />
                <div className="size-2.5 rounded-full bg-primary/70" />
                <div className="size-2.5 rounded-full bg-emerald-500/70" />
                <div className="ml-4 flex items-center gap-2 text-xs text-muted-foreground font-mono">
                  <ShieldCheck className="size-3" />
                  <span>SECURE CONNECTION</span>
                </div>
              </div>
              {/* Screen Content */}
              <div className="w-full h-full pt-10 bg-muted relative">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1575346/img/remote_portal_tablet_dashboard.png" alt="Remote Owner Portal Dashboard on Tablet" className="w-full h-full object-cover object-top" />
                {/* Subtle gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-tr from-black/20 to-transparent pointer-events-none" />
              </div>
            </div>

            {/* Mobile Phone Frame (Mobile App) */}
            <div className="absolute -bottom-8 -right-4 sm:-right-8 lg:-right-12 w-[200px] sm:w-[240px] aspect-[9/19] rounded-[2.5rem] border-[6px] border-secondary bg-card shadow-2xl overflow-hidden z-30 transform transition-transform hover:-translate-y-2 duration-500">
              {/* Notch */}
              <div className="absolute top-0 inset-x-0 h-5 bg-secondary rounded-b-xl w-1/2 mx-auto z-20" />
              {/* Screen Content */}
              <div className="w-full h-full bg-muted relative">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1575346/img/remote_portal_mobile_app.png" alt="Remote Owner Portal Mobile App" className="w-full h-full object-cover object-top" />
              </div>
            </div>
          </div>

          {/* Right Column: Content */}
          <div className="lg:col-span-5 flex flex-col justify-center pt-12 lg:pt-0 lg:pl-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-bold tracking-wide uppercase mb-6 w-fit">
              <Activity className="size-4" />
              <span>Real-Time Telemetry</span>
            </div>
            
            <h2 className="text-4xl sm:text-5xl font-sans font-black tracking-tight text-foreground mb-6 uppercase leading-[1.1]">
              Eyes on <br className="hidden sm:block" />
              <span className="text-primary">Your Project.</span>
            </h2>
            
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">No matter where you are, knowing your project is progressing smoothly and on schedule is important. We bridge the gap with full transparency. Our digital project logs keep you connected to your site's progress.</p>

            <Tabs defaultValue="daily-logs" className="w-full mb-10">
              <TabsList className="grid w-full grid-cols-3 bg-secondary/50 p-1 rounded-lg border border-border/50">
                <TabsTrigger value="daily-logs" className="text-xs sm:text-sm font-bold uppercase tracking-wider">Logs</TabsTrigger>
                <TabsTrigger value="photo-feed" className="text-xs sm:text-sm font-bold uppercase tracking-wider">Photos</TabsTrigger>
                <TabsTrigger value="gps-tracking" className="text-xs sm:text-sm font-bold uppercase tracking-wider">Docs</TabsTrigger>
              </TabsList>
              
              <div className="mt-6 min-h-[140px]">
                <TabsContent value="daily-logs" className="m-0">
                  <div className="p-6 rounded-xl bg-secondary/20 border border-border/50 backdrop-blur-sm">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-md bg-primary/20 text-primary">
                        <FileText className="size-5" />
                      </div>
                      <h3 className="text-xl font-bold text-foreground">Estimates &amp; Reports</h3>
                    </div>
                    <p className="text-muted-foreground text-sm leading-relaxed">Access your past and current jobs details and documentation directly from your portal.</p>
                  </div>
                </TabsContent>
                
                <TabsContent value="photo-feed" className="m-0">
                  <div className="p-6 rounded-xl bg-secondary/20 border border-border/50 backdrop-blur-sm">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-md bg-primary/20 text-primary">
                        <Camera className="size-5" />
                      </div>
                      <h3 className="text-xl font-bold text-foreground">Visual Progress Feed</h3>
                    </div>
                    <p className="text-muted-foreground text-sm leading-relaxed">Our team will update your job files with high-resolution ground-level progress photos directly to your secure portal. Watch your shoreline or foundation take shape in real-time.</p>
                  </div>
                </TabsContent>
                
                <TabsContent value="gps-tracking" className="m-0">
                  <div className="p-6 rounded-xl bg-secondary/20 border border-border/50 backdrop-blur-sm">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-md bg-primary/20 text-primary">
                        <MapPin className="size-5" />
                      </div>
                      <h3 className="text-xl font-bold text-foreground">Estimates, Invoices &amp; Files</h3>
                    </div>
                    <p className="text-muted-foreground text-sm leading-relaxed">Get access to all your documents and files. Even upload plan PDF's and images.</p>
                  </div>
                </TabsContent>
              </div>
            </Tabs>

            <div>
              <Link to="/RemoteOwnerPortalPage">
                <Button size="lg" className="w-full sm:w-auto font-bold tracking-wide uppercase group">
                  Access Owner Portal
                  <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>

        </div>
      </div>
    </section>;
}